import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertTitle, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography } from "@mui/material";
import SellOutlined from "@mui/icons-material/SellOutlined";
import { MonitorButton, MonitorDialog } from "../../components/MonitorControls";
import { api, v } from "../../utils/helpers";
import StageErrorAlert from "./StageErrorAlert";

// requestId der Route ist hex32 (Vertrag 26.3).
const hex32 = () => Array.from(window.crypto.getRandomValues(new Uint8Array(16)), byte => byte.toString(16).padStart(2, "0")).join("");

/**
 * „Topic ändern“ in „Stand je Stufe“ für ein oder mehrere Pakete (Vertrag 26.3). Die Topic-Sperre ist seit 27.10.2026 nur
 * ein Hinweis (Entscheidung David): Der Knopf bleibt aktiv, daneben steht, zu welchem Topic das Paket noch gehört. Der Dialog bietet nur nicht archivierte Topics an und schreibt über
 * POST /api/cicd/transport/stage-topics.
 */
export default function TopicChangeControl({ spaceId, topics = [], packages = [], onChanged }) {
  const { t } = useTranslation("freigabe");
  const titleId = useId();
  const [open, setOpen] = useState(false);
  const [topicId, setTopicId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const topicName = id => topics.find(topic => topic.id === id)?.name || t("stageBranches.topicChange.unknownTopic");
  const locked = packages.filter(pkg => pkg.topicLock?.locked);
  const selectable = topics.filter(topic => !topic.archived);
  const several = packages.length > 1;
  // Hinweis statt Sperrgrund: das bisherige Topic fachlich benannt, Ändern bleibt möglich.
  const reasonOf = pkg => t(pkg.topicLock.reason === "open-request" ? "stageBranches.topicChange.lockedRequestHint" : "stageBranches.topicChange.lockedHint",
    { topicName: topicName(pkg.topicId) });
  // Dasselbe Topic, das alle gewählten Pakete schon haben, ändert nichts.
  const unchanged = !!topicId && packages.every(pkg => pkg.topicId === topicId);
  const disabled = !packages.length || typeof onChanged !== "function";

  const start = () => { if (disabled) return; setTopicId(""); setError(null); setOpen(true); };
  const close = () => { if (!busy) setOpen(false); };
  async function save() {
    if (!topicId || busy || unchanged) return;
    setBusy(true); setError(null);
    try {
      const result = await api("/api/cicd/transport/stage-topics", { method: "POST", body: JSON.stringify({
        spaceId, packageIds: packages.map(pkg => pkg.packageId), topicId, requestId: hex32() }) });
      if (!alive.current) return;
      setOpen(false);
      onChanged(result);
    } catch (failure) {
      if (alive.current) setError({ code: failure?.code || null, message: failure?.message || "", details: failure?.details });
    } finally { if (alive.current) setBusy(false); }
  }

  return <Stack className="sb-topic-change-control" spacing={0.75} alignItems="flex-start">
    <MonitorButton className="promotion-head-button sb-topic-change-button" variant="outlined" startIcon={<SellOutlined/>} aria-haspopup="dialog" disabled={disabled} onClick={start}>
      {t("stageBranches.topicChange.button")}
    </MonitorButton>
    {/* Hinweis neben dem Knopf, bei mehreren Paketen mit Paketname. */}
    {locked.length > 0 && <Stack className="sb-topic-lock-reasons" spacing={0.5} role="status">
    {locked.map(pkg => <Typography key={pkg.packageId} component="span" variant="body2" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
      {several ? `${pkg.packageName || pkg.packageId}: ` : ""}{reasonOf(pkg)}
    </Typography>)}
    </Stack>}
    {open && <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="sm" aria-labelledby={titleId} onClose={close}>
      <DialogTitle id={titleId}>{t("stageBranches.topicChange.dialogTitle")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.topicChange.intro")}</Typography>
          <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {packages.map(pkg => <Typography component="li" variant="body2" key={pkg.packageId} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              <strong>{pkg.packageName || pkg.packageId}</strong>{" "}
              <Typography component="span" variant="body2" sx={{ color: v("text-2") }}>
                {pkg.topicId ? t("stageBranches.topicChange.current", { topicName: topicName(pkg.topicId) }) : t("stageBranches.topicChange.noTopic")}
              </Typography>
            </Typography>)}
          </Stack>
          {selectable.length ? <TextField select fullWidth size="small" label={t("stageBranches.topicChange.topic")} value={topicId} disabled={busy}
            onChange={event => setTopicId(event.target.value)}
            helperText={unchanged ? t("stageBranches.topicChange.sameTopic") : undefined}
            // Das Menü liegt im Portal außerhalb der Seite; der .iio-Scope trägt Farben und Schrift (wie PromotionPackageTopic).
            SelectProps={{ MenuProps: { PaperProps: { className: "iio iio-monitor-menu" } } }}>
            {selectable.map(topic => <MenuItem key={topic.id} value={topic.id}>{topic.name}</MenuItem>)}
          </TextField> : <Alert severity="warning"><AlertTitle>{t("stageBranches.topicChange.noTopics")}</AlertTitle>
            {t("stageBranches.topicChange.noTopicsHint")}</Alert>}
          <StageErrorAlert error={error} title={t("stageBranches.topicChange.error")}/>
        </Stack>
      </DialogContent>
      <DialogActions>
        <MonitorButton autoFocus disabled={busy} onClick={close}>{t("stageBranches.topicChange.cancel")}</MonitorButton>
        <MonitorButton variant="contained" disabled={busy || !topicId || unchanged} onClick={save}>
          {t(busy ? "stageBranches.topicChange.saving" : "stageBranches.topicChange.save")}
        </MonitorButton>
      </DialogActions>
    </MonitorDialog>}
  </Stack>;
}
